import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import {
  ArrowRight,
  Eye,
  ChevronLeft,
  ChevronRight,
  ShoppingBag,
} from 'lucide-react';
import { numberWithCommas } from '../../utils/validations';
import henceforthApi from '../../utils/henceforthApi';

export interface RecentOrder {
  _id?: string;
  order_id?: string;
  user?: {
    name?: string;
    email?: string;
    phone?: string;
    profile_pic?: string;
  };
  user_name?: string;
  customer?: string;
  email?: string;
  created_at?: string | number | Date;
  order_date?: string | number | Date;
  createdAt?: string | number | Date;
  created?: string | number | Date;
  total_price?: number;
  net_amount?: number;
  total_amount?: number;
  amount?: number;
  order_status?: string;
  status?: string;
  order_quantity?: number;
  items_count?: number;
  order_items?: any[];
}

export interface RecentOrdersTableProps {
  orders: RecentOrder[];
  loading?: boolean;
  totalCount?: number;
  pageSize?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  title?: string;
  subtitle?: string;
  viewAllLink?: string;
  className?: string;
}

const RecentOrdersTable: React.FC<RecentOrdersTableProps> = ({
  orders = [],
  loading = false,
  totalCount,
  pageSize = 5,
  currentPage: controlledPage,
  onPageChange,
  title = 'Recent Orders Activity',
  subtitle,
  viewAllLink = '/orders/1',
  className = '',
}) => {
  const [internalPage, setInternalPage] = useState(1);
  const activePage = controlledPage !== undefined ? controlledPage : internalPage;

  const total = totalCount !== undefined ? totalCount : orders.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages || newPage === activePage) return;
    if (onPageChange) {
      onPageChange(newPage);
    } else {
      setInternalPage(newPage);
    }
  };

  // If client-side paginated:
  const isClientSidePaginated = orders.length > pageSize;
  const displayedOrders = isClientSidePaginated
    ? orders.slice((activePage - 1) * pageSize, activePage * pageSize)
    : orders;

  const startRecord = total === 0 ? 0 : (activePage - 1) * pageSize + 1;
  const endRecord = Math.min(startRecord + displayedOrders.length - 1, total);

  // Helper for customer initials
  const getInitials = (name: string) => {
    if (!name || name === 'Customer') return 'CU';
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  // Helper for status badge styling
  const getStatusBadge = (rawStatus: string) => {
    const status = (rawStatus || 'PLACED').toUpperCase();
    switch (status) {
      case 'DELIVERED':
      case 'COMPLETED':
        return {
          pill: 'bg-emerald-50 text-emerald-700 border-emerald-200/70',
          dot: 'bg-emerald-500',
          label: status,
        };
      case 'PLACED':
      case 'ACCEPTED':
      case 'CONFIRMED':
      case 'ACTIVE':
        return {
          pill: 'bg-sky-50 text-sky-700 border-sky-200/70',
          dot: 'bg-[#1AA7DE]',
          label: status,
        };
      case 'CANCELLED':
      case 'REJECTED':
      case 'FAILED':
        return {
          pill: 'bg-rose-50 text-rose-700 border-rose-200/70',
          dot: 'bg-rose-500',
          label: status,
        };
      case 'PENDING':
      case 'IN_TRANSIT':
      case 'PROCESSING':
        return {
          pill: 'bg-amber-50 text-amber-700 border-amber-200/70',
          dot: 'bg-amber-500',
          label: status,
        };
      default:
        return {
          pill: 'bg-slate-100 text-slate-700 border-slate-200/70',
          dot: 'bg-slate-400',
          label: status,
        };
    }
  };

  return (
    <div
      className={`bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] overflow-hidden transition-all duration-200 flex flex-col justify-between ${className}`}
    >
      {/* Table Card Header with generous padding */}
      <div className="px-6 py-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-gradient-to-b from-white to-slate-50/40">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h3 className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight m-0">
              {title}
            </h3>
          </div>
          {subtitle && (
            <p className="text-xs sm:text-sm text-slate-500 font-normal mt-0.5 m-0">
              {subtitle}
            </p>
          )}
        </div>

        {viewAllLink && (
          <Link
            to={viewAllLink}
            className="text-sm font-semibold text-[#1AA7DE] hover:text-[#148bb9] inline-flex items-center gap-1.5 transition-colors group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1AA7DE] rounded-md px-1 py-0.5 -mx-1"
          >
            <span>View All Orders</span>
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        )}
      </div>

      {/* Main Table Content */}
      <div className="overflow-x-auto w-full">
        <table className="w-full text-left border-collapse">
          {/* Light modern header with uppercase tracking */}
          <thead className="bg-slate-50/80 border-b border-slate-100">
            <tr>
              <th
                scope="col"
                className="px-6 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap"
              >
                Order ID
              </th>
              <th
                scope="col"
                className="px-6 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap"
              >
                Customer
              </th>
              <th
                scope="col"
                className="px-6 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap"
              >
                Date
              </th>
              <th
                scope="col"
                className="px-6 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap text-right"
              >
                Amount
              </th>
              <th
                scope="col"
                className="px-6 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap text-center"
              >
                Status
              </th>
              <th
                scope="col"
                className="px-6 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap text-right"
              >
                Action
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100 bg-white">
            {loading ? (
              /* Shimmer Loading Skeleton */
              [1, 2, 3, 4, 5].map((item) => (
                <tr key={item} className="animate-pulse">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="w-24 h-4 bg-slate-200 rounded-md"></div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-slate-200 shrink-0"></div>
                      <div className="flex flex-col gap-1.5">
                        <div className="w-28 h-3.5 bg-slate-200 rounded"></div>
                        <div className="w-36 h-2.5 bg-slate-100 rounded"></div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="w-20 h-3.5 bg-slate-200 rounded"></div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="w-16 h-4 bg-slate-200 rounded ml-auto"></div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="w-20 h-6 bg-slate-200 rounded-full mx-auto"></div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="w-7 h-7 bg-slate-100 rounded-lg ml-auto"></div>
                  </td>
                </tr>
              ))
            ) : displayedOrders.length === 0 ? (
              /* Empty State */
              <tr>
                <td colSpan={6} className="px-6 py-16 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="w-14 h-14 rounded-2xl bg-sky-50 text-[#1AA7DE] border border-sky-100 flex items-center justify-center mb-3 shadow-2xs">
                      <ShoppingBag className="w-7 h-7" />
                    </div>
                    <p className="text-base font-bold text-slate-800 m-0">No orders placed yet</p>
                    <p className="text-xs sm:text-sm text-slate-400 max-w-sm mt-1 mb-0 leading-relaxed">
                      Recent customer orders will automatically appear here once incoming activity begins.
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              /* Data Rows */
              displayedOrders.map((order, index) => {
                const orderId =
                  order.order_id ||
                  (order._id ? String(order._id).slice(-6).toUpperCase() : `ORD-${index + 1}`);

                const orderUrl = order._id ? `/order/${order._id}` : '#';

                const customerName =
                  order.user?.name ||
                  order.user_name ||
                  order.customer ||
                  'Customer';

                const customerEmail =
                  order.user?.email ||
                  order.email ||
                  order.user?.phone ||
                  '';

                const avatarPic = order.user?.profile_pic
                  ? `${henceforthApi.API_FILE_ROOT_SMALL}${order.user.profile_pic}`
                  : null;

                const initials = getInitials(customerName);

                const rawDate =
                  order.created_at ||
                  order.order_date ||
                  order.createdAt ||
                  order.created;

                const dateFormatted = rawDate
                  ? moment(rawDate).format('MMM D, YYYY')
                  : '—';

                const amountNum = Number(
                  order.total_price ??
                  order.net_amount ??
                  order.total_amount ??
                  order.amount ??
                  0
                );

                const statusBadge = getStatusBadge(order.order_status || order.status || '');

                return (
                  <tr
                    key={order._id || index}
                    className="hover:bg-slate-50/70 transition-colors duration-150 group"
                  >
                    {/* Order ID */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold">
                      <Link
                        to={orderUrl}
                        className="text-[#1AA7DE] hover:text-[#148bb9] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1AA7DE] rounded px-1 py-0.5 -mx-1 transition-colors"
                      >
                        #{orderId}
                      </Link>
                    </td>

                    {/* Customer */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        {avatarPic ? (
                          <img
                            src={avatarPic}
                            alt={customerName}
                            className="w-9 h-9 rounded-full object-cover border border-slate-200 shrink-0"
                          />
                        ) : (
                          <span className="w-9 h-9 rounded-full bg-gradient-to-tr from-sky-50 to-blue-100 text-[#1AA7DE] font-bold text-xs flex items-center justify-center shrink-0 border border-sky-200/50 shadow-2xs">
                            {initials}
                          </span>
                        )}
                        <div className="flex flex-col overflow-hidden">
                          <span
                            className="text-sm font-semibold text-slate-800 truncate max-w-[170px]"
                            title={customerName}
                          >
                            {customerName}
                          </span>
                          {customerEmail && (
                            <span
                              className="text-xs text-slate-400 font-normal truncate max-w-[170px]"
                              title={customerEmail}
                            >
                              {customerEmail}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Date */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 font-medium">
                      {dateFormatted}
                    </td>

                    {/* Amount */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-slate-900 text-right tabular-nums">
                      ${numberWithCommas(amountNum)}
                    </td>

                    {/* Status Badge */}
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${statusBadge.pill} whitespace-nowrap shadow-2xs`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${statusBadge.dot} shrink-0`} />
                        <span>{statusBadge.label}</span>
                      </span>
                    </td>

                    {/* Action */}
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <Link
                        to={orderUrl}
                        aria-label={`View order ${orderId}`}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-[#1AA7DE] hover:bg-sky-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1AA7DE]"
                        title="View order details"
                      >
                        <Eye className="w-4 h-4" />
                      </Link>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RecentOrdersTable;
